import useLocalStorage, { STORAGE_KEY } from '@hooks/useLocalStorage';
import pathnames from '@src/app/routes/pathnames';
import log from '@src/datadog';
import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useAuth } from 'react-oidc-context';

interface AuthenticationContextProps {
  doLogout?: () => void;
}

const AuthenticationContext = createContext<AuthenticationContextProps>({});

const AuthenticationContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const auth = useAuth();

  const { removeStorage } = useLocalStorage();

  const doLogout = useCallback(async () => {
    try {
      localStorage.setItem('is_logging_out', 'true');

      const idToken = auth.user?.id_token;

      log.clearDDUser();
      removeStorage(STORAGE_KEY.MARKETING_ID());

      await auth.removeUser();
      await auth.revokeTokens(['access_token', 'refresh_token']);
      await auth.clearStaleState();

      if (idToken) {
        await auth.signoutRedirect({
          id_token_hint: idToken,
          post_logout_redirect_uri: window.location.origin + pathnames.login(),
        });
      } else {
        window.location.replace(pathnames.login());
      }
    } catch (error) {
      log.sendError('Logout failed', { error });
      localStorage.setItem('is_logging_out', 'true');
      window.location.replace(pathnames.login());
    }
  }, [auth, removeStorage]);

  const authenticationContextValue = useMemo(
    () => ({
      doLogout,
    }),
    [doLogout],
  );

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {!auth.isLoading && children}
    </AuthenticationContext.Provider>
  );
};

const useAuthenticationContext = () => {
  return useContext(AuthenticationContext);
};

export {
  AuthenticationContext,
  AuthenticationContextProvider,
  useAuthenticationContext,
};
